import { positionChecker } from 'src/util/useModelDesign'
import { I18NText, Position, State, StateObj } from 'src/types'
import { aplSeriesChecker, seriesChecker, usModelOfAtomsChecker } from 'src/features/brands/atoms/helper'

const genGloveColorCell = (head: string, stateObj: StateObj, partsKey: string) => ({
  head,
  label: stateObj?.label,
  color: stateObj?.color,
  value: stateObj?.value,
  partsKey
})

// atoms only
export const checkAtomsWeb2 = (webPartsValue: string, position: Position): boolean =>
  [
    'w6',
    'w8',
    'w9',
    'w10',
    'w11',
    'w13',
    'w14',
    'w17',
    'w19',
    'w20',
    'w23',
    'w25',
    'w27',
    'w28',
    'w29',
    'w30',
    'w31',
    'w32',
    'w33',
    'w34',
    'w36'
  ].includes(webPartsValue) ||
  (webPartsValue === 'w26' && position !== 'pitcher') ||
  position === 'first'

// グローブ / 基本項目ロジック
export const getAtomsBaseCells = (state: State, position: Position, t: I18NText) => {
  const genGloveBaseCell = (head: string, stateObj: StateObj) => ({ head, label: stateObj?.label, value: stateObj?.value })
  const { isPitcher, isGlove, isCatcher } = positionChecker(position)
  const productNumber = state.baseModel.productNumber
  const baseModelStateObj = {
    label: productNumber,
    value: productNumber
  }
  const isUsModel = usModelOfAtomsChecker(productNumber)
  const isJapanModel = !isUsModel
  const isAGL_NE105 = state.baseModel.productNumber === 'AGL-NE105'
  return [
    isJapanModel && genGloveBaseCell(t.KINDS, state?.kinds || { label: '', value: '' }), // 保存済み旧シミュレーションは未設定の可能性あり
    genGloveBaseCell(t.BASE_MODEL, baseModelStateObj),
    genGloveBaseCell(t.TANNER, state.tanner),
    genGloveBaseCell(t.DOMINANT_ARM, state.dominantArm),
    isGlove && genGloveBaseCell(t.WEB, state.webParts),
    isPitcher && !isAGL_NE105 && isJapanModel && genGloveBaseCell(t.BACK_STYLE, state.backStyle),
    genGloveBaseCell(t.FINGER_GUARD, state.fingerGuardType),
    isPitcher && !isAGL_NE105 && genGloveBaseCell(t.MATERIAL_PACK, state.materialPack),
    isGlove && genGloveBaseCell(t.FINGER_SIZE, state.size),
    isGlove && genGloveBaseCell(t.BACK_LACE, state.backLace), // 投手以外もOK
    isJapanModel && !isCatcher && genGloveBaseCell(t.LOOP_OF_RING, state.loopOfRingFinger),
    isUsModel && genGloveBaseCell(t.MOLDING_ORDER, state?.moldingOrder)
  ].filter(Boolean)
}

const variableColorPartsGenerator = (state: State, position: Position, t: I18NText) => {
  const productNumber = state.baseModel.productNumber
  const isUsModel = usModelOfAtomsChecker(productNumber)
  const { is2025Apl, isDefaultWristBelt } = seriesChecker(productNumber)
  const isUBack = state.baseModel.isUBack
  const backStyle = state.backStyle.value
  const isBelt = !isUsModel && (is2025Apl || isDefaultWristBelt)

  switch (position) {
    case 'pitcher':
      switch (backStyle) {
        case 'firstBackStyle':
          return [
            genGloveColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
            genGloveColorCell(t.THUMB2_INDEX_MIDDLE5, state.thumbIndexMiddle, 'thumbIndexMiddle'),
            genGloveColorCell(t.MIDDLE6_RING7, state.middleLeftRingRight, 'middleLeftRingRight'),
            genGloveColorCell(t.RING8_LITTLE9, state.ringLeftLittleRight, 'ringLeftLittleRight'),
            genGloveColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
            isBelt && genGloveColorCell(t.BELT, state.listBelt, 'listBelt') // ベルト必須
          ].filter(Boolean)
        case 'crownBackStyle':
          // ベルト一体型を考慮＿済
          return [
            genGloveColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
            genGloveColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb'),
            genGloveColorCell(t.INDEX3, state.indexWeb, 'indexWeb'),
            genGloveColorCell(t.INDEX4_MIDDLE5, state.indexLeftMiddleRight, 'indexLeftMiddleRight'),
            genGloveColorCell(t.MIDDLE6_RING7, state.middleLeftRingRight, 'middleLeftRingRight'),
            genGloveColorCell(t.RING8_LITTLE9, state.ringLeftLittleRight, 'ringLeftLittleRight'),
            genGloveColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
            isBelt && genGloveColorCell(t.BAND, state.listBelt, 'listBelt'),
            genGloveColorCell(t.MOUTON, state.mouton, 'mouton')
          ].filter(Boolean)
        default:
          // case 'beltBackStyle':
          // ベルト一体型を考慮＿済
          return [
            genGloveColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
            genGloveColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb'),
            isUsModel && genGloveColorCell(t.THUMB_ROOT, state.thumbRoot, 'thumbRoot'),
            genGloveColorCell(t.INDEX3, state.indexWeb, 'indexWeb'),
            genGloveColorCell(t.INDEX4, state.indexMiddle, 'indexMiddle'),
            genGloveColorCell(t.MIDDLE5, state.middleIndex, 'middleIndex'),
            genGloveColorCell(t.MIDDLE6, state.middleRing, 'middleRing'),
            genGloveColorCell(t.RING7, state.ringMiddle, 'ringMiddle'),
            genGloveColorCell(t.RING8, state.ringLittle, 'ringLittle'),
            genGloveColorCell(t.LITTLE9, state.littleRing, 'littleRing'),
            genGloveColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
            isBelt && genGloveColorCell(t.BAND, state.listBelt, 'listBelt'),
            genGloveColorCell(t.MOUTON, state.mouton, 'mouton')
          ].filter(Boolean)
      }
    case 'infielder':
    case 'outfielder':
      if (isUBack) {
        return [
          genGloveColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
          genGloveColorCell(t.THUMB2_INDEX3, state.thumbWebAndIndexWeb, 'thumbWebAndIndexWeb'),
          genGloveColorCell(t.INDEX4, state.indexMiddle, 'indexMiddle'),
          genGloveColorCell(t.MIDDLE5, state.middleIndex, 'middleIndex'),
          genGloveColorCell(t.MIDDLE6, state.middleRing, 'middleRing'),
          genGloveColorCell(t.RING7, state.ringMiddle, 'ringMiddle'),
          genGloveColorCell(t.RING8, state.ringLittle, 'ringLittle'),
          genGloveColorCell(t.LITTLE9, state.littleRing, 'littleRing'),
          genGloveColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
          isBelt && genGloveColorCell(t.BAND, state.listBelt, 'listBelt'),
          genGloveColorCell(t.MOUTON, state.mouton, 'mouton')
        ].filter(Boolean)
      }
      return [
        genGloveColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
        genGloveColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb'),
        isUsModel && genGloveColorCell(t.THUMB_ROOT, state.thumbRoot, 'thumbRoot'),
        genGloveColorCell(t.INDEX3, state.indexWeb, 'indexWeb'),
        genGloveColorCell(t.INDEX4, state.indexMiddle, 'indexMiddle'),
        genGloveColorCell(t.MIDDLE5, state.middleIndex, 'middleIndex'),
        genGloveColorCell(t.MIDDLE6, state.middleRing, 'middleRing'),
        genGloveColorCell(t.RING7, state.ringMiddle, 'ringMiddle'),
        genGloveColorCell(t.RING8, state.ringLittle, 'ringLittle'),
        genGloveColorCell(t.LITTLE9, state.littleRing, 'littleRing'),
        genGloveColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
        isBelt && genGloveColorCell(t.BAND, state.listBelt, 'listBelt'),
        genGloveColorCell(t.MOUTON, state.mouton, 'mouton')
      ].filter(Boolean)
    default:
      return []
  }
}

// グローブ / カラー項目ロジック
export const getAtomsGloveColorCells = (state: State, position: Position, t: I18NText) => {
  const needFingerCoverOrPad = state.fingerGuardType?.value !== 'none'
  const needWeb2 = checkAtomsWeb2(state.webParts?.value, position)
  const variablePartsCells = variableColorPartsGenerator(state, position, t)
  return [
    genGloveColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    genGloveColorCell(t.WEB, state.web, 'web'),
    needWeb2 && genGloveColorCell(t.WEB2, state.web2, 'web2'),
    genGloveColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    genGloveColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && genGloveColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    genGloveColorCell(t.LININGS, state.linings, 'linings'),
    ...variablePartsCells,
    genGloveColorCell(t.LACE, state.lace, 'lace'),
    genGloveColorCell(t.EDGE, state.edge, 'edge'),
    genGloveColorCell(t.STITCH, state.stitch, 'stitch'),
    genGloveColorCell(t.HAMIDASHI, state.hamidashi, 'hamidashi'),
    genGloveColorCell(t.LABEL, state.atomsLabel, 'atomsLabel')
  ].filter(Boolean)
}

// ファーストミット / カラー項目ロジック
export const getAtomsFirstMittColorCells = (state: State, t: I18NText) => {
  const genFirstMittColorCell = (head: string, stateObj: StateObj, partsKey: string) => ({
    head,
    label: stateObj?.label,
    color: stateObj?.color,
    value: stateObj?.value,
    partsKey
  })

  const isAplSeries = aplSeriesChecker(state.baseModel.productNumber)
  const needFingerCoverOrPad = state.fingerGuardType.value !== 'none'
  return [
    genFirstMittColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    genFirstMittColorCell(t.WEB, state.web, 'web'),
    genFirstMittColorCell(t.WEB2, state.web2, 'web2'),
    genFirstMittColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    genFirstMittColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && genFirstMittColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    genFirstMittColorCell(t.LININGS, state.linings, 'linings'),
    genFirstMittColorCell(t.THUMB, state.thumb, 'thumb'),
    genFirstMittColorCell(t.UNDER_WEB, state.underWeb, 'underWeb'),
    genFirstMittColorCell(t.BOOMERANG, state.boomerang, 'boomerang'),
    !isAplSeries && genFirstMittColorCell(t.BAND, state.listBelt, 'listBelt'),
    genFirstMittColorCell(t.LACE, state.lace, 'lace'),
    genFirstMittColorCell(t.EDGE, state.edge, 'edge'),
    genFirstMittColorCell(t.STITCH, state.stitch, 'stitch'),
    genFirstMittColorCell(t.MOUTON, state.mouton, 'mouton'),
    genFirstMittColorCell(t.LABEL, state.atomsLabel, 'atomsLabel')
  ].filter(Boolean)
}

// キャッチャーミット / カラー項目ロジック
export const getAtomsCatcherMittColorCells = (state: State, t: I18NText) => {
  const genCatcherMittColorCell = (head: string, stateObj: StateObj, partsKey: string) => ({
    head,
    label: stateObj?.label,
    color: stateObj?.color || 'No data',
    value: stateObj?.value,
    partsKey
  })

  const needFingerCoverOrPad = state.fingerGuardType.value !== 'none'
  const backStyleValue = state.baseModel.backStyle
  const isRegularBackStyle = backStyleValue === 'regularBackStyle'
  const isBeltBackStyle = backStyleValue === 'beltBackStyle'

  return [
    genCatcherMittColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    genCatcherMittColorCell(t.LACE, state.lace, 'lace'),
    genCatcherMittColorCell(t.WEB, state.web, 'web'),
    genCatcherMittColorCell(t.EDGE, state.edge, 'edge'),
    genCatcherMittColorCell(t.STITCH, state.stitch, 'stitch'),
    genCatcherMittColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    genCatcherMittColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && genCatcherMittColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    genCatcherMittColorCell(t.LININGS, state.linings, 'linings'),
    genCatcherMittColorCell(t.STAND, state.fingerStand, 'fingerStand'),
    genCatcherMittColorCell(t.THUMB_INDEX_MIDDLE5, state.thumbIndexMiddleRight, 'thumbIndexMiddleRight'), // 親指・人差指・中指5
    isRegularBackStyle && genCatcherMittColorCell(t.MIDDLE5_RING, state.middleLeftRing, 'middleLeftRing'),
    isRegularBackStyle && genCatcherMittColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
    isRegularBackStyle && genCatcherMittColorCell(t.FINGER_TIP_OUT, state.indexFingerTipOut, 'indexFingerTipOut'),
    isRegularBackStyle && genCatcherMittColorCell(t.BELT, state.listBelt, 'listBelt'),
    isBeltBackStyle && genCatcherMittColorCell(t.MIDDLE6_RING_PINKY, state.middleLeftRingLittle, 'middleLeftRingLittle'),
    genCatcherMittColorCell(t.HAMIDASHI, state.hamidashi, 'hamidashi'),
    genCatcherMittColorCell(t.TARGET, state.catchFaceTarget, 'catchFaceTarget'),
    isBeltBackStyle && genCatcherMittColorCell(t.MOUTON, state.mouton, 'mouton'),
    genCatcherMittColorCell(t.LABEL, state.atomsLabel, 'atomsLabel')
  ].filter(Boolean)
}
